import { useState } from 'react'
import { useCreateDriverBoardInvite } from '../../hooks/backend/performance'
import { useCapabilities } from '../../hooks/useCapabilities'

/**
 * Creates the one-time link that connects a driver's Google account to his
 * board, and copies it. A driver's account is only ever connected this way,
 * never by matching his email (docs/DRIVER-AUTH.md), so this button is the
 * staff half of every driver's first sign-in and of every re-link after a lost
 * phone. The server refuses it without permission to edit drivers.
 *
 * It lives here, and not only on the Competition page, because on 6 October
 * 2026 not one link had ever been made (Platform Request #34): staff working
 * from Active Drivers had no way to find it. `linked` is optional because
 * Active Drivers does not load the board's link state; when it is unknown the
 * button says nothing about it rather than guess.
 */
export function BoardLinkButton({ driverId, linked }: { driverId: string; linked?: boolean }) {
  const { canEditDrivers, isAdmin } = useCapabilities()
  const create = useCreateDriverBoardInvite()
  const [link, setLink] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const known = linked !== undefined
  if (!(canEditDrivers || isAdmin)) {
    return known ? <span className="text-xs text-muted-foreground">{linked ? 'Signed in' : 'Not yet'}</span> : null
  }
  async function make() {
    setMessage(null)
    try {
      const res = (await create.trigger({ driver_id: driverId })) as { code?: string } | null
      if (!res?.code) throw new Error('No link came back. Try again.')
      const url = `${window.location.origin}/board?invite=${res.code}`
      setLink(url)
      try {
        await navigator.clipboard.writeText(url)
        setMessage('Copied. Send it to him on WhatsApp.')
      } catch {
        setMessage('Copy the link and send it to him on WhatsApp.')
      }
    } catch (e) {
      setMessage(e instanceof Error ? e.message : String(e))
    }
  }
  return (
    <div className="flex min-w-40 flex-col gap-1">
      <div className="flex items-center gap-2">
        {known && <span className="text-xs text-muted-foreground">{linked ? 'Signed in' : 'Not yet'}</span>}
        <button
          type="button"
          onClick={make}
          disabled={create.loading}
          className="rounded-md border border-border px-2 py-0.5 text-xs hover:bg-muted disabled:opacity-60"
        >
          {linked ? 'New link' : known ? 'Sign-in link' : 'Driver board sign-in link'}
        </button>
      </div>
      {link && <input readOnly value={link} className="w-full rounded border border-border bg-background px-1 text-[11px]" onFocus={(e) => e.currentTarget.select()} />}
      {message && <span className="text-[11px] text-muted-foreground">{message}</span>}
    </div>
  )
}
